import { Box, Grid, Divider, Button } from "@mui/material";
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import CreditCardSection from "./CreditCardSelection";
import FullscreenImageDialog from "./FullviewImage";
import ImageCarousel from "./ImageCarousel";
import PaymentDialog from "./PaymentDialog";
import ProductBasicInfo from "./ProductBasicInfo";
import QuantitySelector from "./QuantitySelector";
import ShippingCalculator from "./ShippingCalculator";
import StoreInfo from "./StoreInfo";
import { useProductDetail } from "./useProductDetail";
import Breadcrumbs from "./Breadcrumbs";
import ProductDescription from "./ProductDescription";
import RelatedProductsCarousel from "./RelatedProductsCarousel"; 
import { ProductWithImages } from "../../types/Product";
import { ProductVariantAttributeWithValues, ProductVariant, ProductVariantGetByAttributesValuesRequestDTO } from "../../types/ProductVariant";
import { BUTTON_STYLES } from "../../styles/buttonStyles";
import { AppConfigFields } from "../../types/appConfigEntities";
import ProductVariantSelector from "./ProductVariantSelector";
import { HttpProduct, HttpCategory } from "../../http";



interface BreadcrumbItem {
  label: string;
  href: string;
}

interface ProductDetailProps {
  productWithImages: ProductWithImages;
  variantAttributes?: ProductVariantAttributeWithValues[];
}


const minimumPurchase = window.APP_CONFIG?.[AppConfigFields.MinimumPurchaseQuantity];

export default function ProductDetail({ productWithImages, variantAttributes }: ProductDetailProps) {
  const location = useLocation();
  const productImages = productWithImages.imagenesAdicionales
    .sort((a, b) => a.posicion - b.posicion)
    .map(img => img.urlImagen);
  const productInfo = productWithImages.producto;

  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [selectedVariantAttributes, setSelectedVariantAttributes] = useState<{ name: string; value: string }[]>([]);

  const minQuantity = minimumPurchase || 1;

  const [quantity, setQuantity] = useState(minQuantity);

    useEffect(() => {
        const variantStock = selectedVariant?.stock ?? productInfo.stock ?? 0;

        const safeMin = Math.min(minQuantity, variantStock);

        setQuantity(safeMin);
    }, [selectedVariant?.id, productInfo.id]);


    const handleQuantityChange = (value: number) => {
        const stock = selectedVariant?.stock ?? productInfo.stock ?? 0;

        const safeValue = Math.min(
            Math.max(value, minQuantity),
            stock
        );

        setQuantity(safeValue);
    };


  const buildBreadcrumbs = async (categoryId: string, allCategories: any[], fromCategorySku?: string) => {
    const crumbs: BreadcrumbItem[] = [{ label: 'Inicio', href: '/' }];

    if (fromCategorySku) {
      const categoryName = fromCategorySku
        .split('-')
        .map(word => word.toUpperCase())
        .join(' ');
      crumbs.push({ label: categoryName, href: `/categoria/${fromCategorySku}` });
      crumbs.push({ label: productInfo.nombre, href: `/productos/${productInfo.sku}` });
      return crumbs;
    }

    const buildCrumb = (catId: string | null) => {
      if (!catId) return;
      const cat = allCategories.find(c => c.id === catId);
      if (cat) {
        buildCrumb(cat.idCategoriaPadre);
        crumbs.push({ label: cat.nombre, href: `/categoria/${cat.sku}` });
      }
    };

    buildCrumb(categoryId);
    crumbs.push({ label: productInfo.nombre, href: `/productos/${productInfo.sku}` });
    return crumbs;
  };

  useEffect(() => {
    const fetchBreadcrumbs = async () => {
      const fromCategorySku = location.state?.fromCategorySku as string | undefined;

      if (productInfo.categoriasIds && productInfo.categoriasIds.length > 0) {
        try {
          const allCategories = await HttpCategory.getAllCategories();
          const crumbs = await buildBreadcrumbs(productInfo.categoriasIds[0], allCategories, fromCategorySku);
          setBreadcrumbs(crumbs);
        } catch (error) {
          console.error('Error fetching categories for breadcrumbs:', error);
          setBreadcrumbs([{ label: 'Inicio', href: '/' }, { label: productInfo.nombre, href: `/productos/${productInfo.sku}` }]);
        }
      } else {
        setBreadcrumbs([{ label: 'Inicio', href: '/' }, { label: productInfo.nombre, href: `/productos/${productInfo.sku}` }]);
      }
    };

    fetchBreadcrumbs();
  }, [productInfo, location.state]);

   const {
    //quantity,
    selectedImage,
    paymentDialogOpen,
    fullscreenDialogOpen,
    //handleQuantityChange,
    handleCalculateShipping,
    nextImage,
    prevImage,
    setSelectedImage,
    openPaymentDialog,
    closePaymentDialog,
    openFullscreenDialog,
    closeFullscreenDialog,
  } = useProductDetail(productInfo, productImages);

  const handleDisabledClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});

    useEffect(() => {
        if (!variantAttributes || variantAttributes.length === 0) return;

        const atributosSeleccionados = Object.keys(selectedAttributes);

        if (atributosSeleccionados.length !== variantAttributes.length) {
            setSelectedVariant(null);
            setSelectedVariantAttributes([]);
            return;
        }

        const requestBody = {
            idsProductoAtributos: atributosSeleccionados,
            idsProductoAtributoValores: atributosSeleccionados.map(
                attrId => selectedAttributes[attrId]
            )
        };

        const fetchVariant = async () => {
            try {
                const response = await HttpProduct.getProductVariantByAttributesValues(
                    productInfo.id,
                    requestBody
                );

                if (response && response.length > 0) {
                    const detail = await HttpProduct.getProductVariantDetail(response[0].id);

                    setSelectedVariant(detail.productoVariante);

                    setSelectedVariantAttributes(
                        detail.productoVarianteAtributoValores.map(v => ({
                            name: v.nombreAtributo,
                            value: v.nombreAtributoValor
                        }))
                    );

                } else {
                    setSelectedVariant(null);
                    setSelectedVariantAttributes([]);
                }
            } catch (error) {
                console.error("Error fetching variant:", error);
                setSelectedVariant(null);
                setSelectedVariantAttributes([]);
            }
        };

        fetchVariant();
    }, [selectedAttributes, variantAttributes, productInfo.id]);


    const handleAttributeChange = (attributeId: string, valueId: string) => {
        setSelectedAttributes(prev => ({
            ...prev,
            [attributeId]: valueId
        }));
    };

    


    useEffect(() => {
        console.log("Selected variant:", selectedVariant);
    }, [selectedVariant]);

    // Caso borde: el producto NO tiene atributos -> cargar variante default igual
    useEffect(() => {
        console.log("PRODUCT INFO", productInfo);
        if (!productInfo || !productInfo.idProductoVarianteDefault) return;

        // Si NO hay atributos ? cargar variante default directamente
        console.log("VARIANT ATTRIBUTES", variantAttributes);
        if (variantAttributes && variantAttributes.length === 0) {
            const loadVariant = async () => {
                try {
                    const variantDetail = await HttpProduct.getProductVariantDetail(
                        productInfo.idProductoVarianteDefault
                    );

                    console.log("VARIANT DETAILS", variantDetail);

                    setSelectedVariant(variantDetail.productoVariante);
                    setSelectedVariantAttributes(
                        variantDetail.productoVarianteAtributoValores.map(v => ({
                            name: v.nombreAtributo,
                            value: v.nombreAtributoValor
                        }))
                    );
                    // No seteamos selectedAttributes porque no hay atributos
                } catch (error) {
                    console.error("Error loading default variant (no attributes):", error);
                }
            };

            loadVariant();
        }
    }, [productInfo, variantAttributes]);


    // Inicializar variante por defecto al cargar
    useEffect(() => {
        if (!productInfo || !productInfo.idProductoVarianteDefault) return;
        if (!variantAttributes || variantAttributes.length === 0) return;

        const loadDefaultVariant = async () => {
            try {
                const variantDetail = await HttpProduct.getProductVariantDetail(
                    productInfo.idProductoVarianteDefault
                );

                // Guardamos la variante seleccionada
                setSelectedVariant(variantDetail.productoVariante);
                setSelectedVariantAttributes(
                    variantDetail.productoVarianteAtributoValores.map(v => ({
                        name: v.nombreAtributo,
                        value: v.nombreAtributoValor
                    }))
                );

                // Preparamos selección automática en los selectores
                const initialSelected: Record<string, string> = {};

                for (const attrVal of variantDetail.productoVarianteAtributoValores) {
                    const attr = variantAttributes.find(
                        a => a.nombreAtributo === attrVal.nombreAtributo
                    );
                    if (!attr) continue;

                    const value = attr.valores.find(
                        v => v.nombreValor === attrVal.nombreAtributoValor
                    );
                    if (!value) continue;

                    initialSelected[attr.idProductoAtributo] = value.idProductoAtributoValor;
                }

                setSelectedAttributes(initialSelected);
            } catch (error) {
                console.error("Error loading default variant", error);
            }
        };

        loadDefaultVariant();
    }, [productInfo, variantAttributes]);




  return (
    <>
      <Grid container spacing={0} sx={{ display: 'flex' }}>
        {/* Product Images - Left side */}
        <Grid size={{ xs: 12, md: 8 }}>
          <ImageCarousel
            images={productImages}
            productName={productInfo.nombre}
            productStock={productInfo.stock || 0}
            onImageClick={openFullscreenDialog}
            selectedImage={selectedImage}
            onImageSelect={setSelectedImage}
            onNextImage={nextImage}
            onPrevImage={prevImage}
          />
        </Grid>

        {/* Vertical Divider */}
        <Divider 
          orientation="vertical" 
          flexItem 
          sx={{ display: { xs: 'none', md: 'block' }, borderColor: 'grey.300' }} 
        />

        {/* Product Info - Right side */}
        <Grid size={{ xs: 12, md: 3.8 }}>
          <Box sx={{ textAlign: "left", pt: 4, pl: 4 }}>
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            
            <ProductBasicInfo product={productInfo} variant={selectedVariant} />
            
            <CreditCardSection onClick={openPaymentDialog} />
            {(/*productInfo.stock && productInfo.stock > 0*/ true) ? 
              <>

                <ProductVariantSelector
                  variantAttributes={variantAttributes || []}
                  selectedAttributes={selectedAttributes}
                  onChange={handleAttributeChange}
                  selectedVariant={selectedVariant}
                />

                <QuantitySelector
                  productId={productInfo.id.toString()}
                  variantId={selectedVariant?.id}
                  productName={productInfo.nombre}
                  productPrice={selectedVariant ? selectedVariant.precio : (productInfo.precio || 0)}
                  productImage={productInfo.urlImagenPrincipal} // CAMBIAR A IMAGEN DE LA VARIANTE CUANDO ESTEN CARGADAS
                  quantity={quantity}
                  stock={selectedVariant ? selectedVariant.stock : (productInfo.stock || 0)}
                  onQuantityChange={handleQuantityChange}
                  minQuantity={minimumPurchase}
                  attributes={selectedVariantAttributes}
                />
                
                <ShippingCalculator
                    onCalculateShipping={handleCalculateShipping}
                    productId={productInfo.id.toString()}
                    productVariantId={selectedVariant?.id}
                    quantity={quantity}
                />
                <StoreInfo />
              </>
            :
              <Button
                variant="contained"
                onClick={handleDisabledClick}
                sx={{
                  ...BUTTON_STYLES.primary,
                  width: '100%',
                  mt: 2,
                  cursor: 'not-allowed',
                  '&:hover': {
                    color: '#dddddd',
                    backgroundColor: 'transparent',
                    cursor: 'not-allowed',
                    '&:before': {
                      height: '100%',
                    }
                  }
                }}
              >
                SIN STOCK
              </Button>
            }
            <ProductDescription product={productInfo} />
          </Box>
        </Grid>
      </Grid>
      
      <Divider sx={{ borderColor: 'grey.300' }} />

      <RelatedProductsCarousel 
        currentProductId={productInfo.id}
        categoryId={productInfo.categoriasIds ? productInfo.categoriasIds[0] : undefined}
      />
      
      {/* Dialogs */}
      <PaymentDialog 
        open={paymentDialogOpen} 
        onClose={closePaymentDialog} 
      />
      
      <FullscreenImageDialog
        open={fullscreenDialogOpen}
        onClose={closeFullscreenDialog}
        images={productImages}
        productName={productInfo.nombre}
        selectedImage={selectedImage}
      />
    </>
  );
}














